import axios from 'axios'
import _ from 'lodash'
import { format } from 'date-fns'

import { convert, mergeByRules } from 'utils/merge'
import { getGlobalParams } from 'config/config'
import fundRules from './FundsList/fundRules'
import dividendRule from './DividendRecords/rule'
import {
  formatFundDetailOverview,
  formatFundDetailDividend,
  formatFundDetailDocument,
  formatFundDetailHoldings,
  formatAnnualReturnData,
  formatCumulativeReturnData,
  formatDividendDocuments,
  formatFundDetailESG,
  convertTokenizers
} from './data.format'

let fundListData = null
let allFunds = null
let splittingWords = null
let contentApiData = null

const getFundList = async () => {
  try {
    const { terms } = getGlobalParams()
    const fundList =
      fundListData ||
      (await axios({
        method: 'GET',
        url: `/xapi/fundList`
      }))

    fundListData = fundList.data || fundList
    allFunds = mergeByRules(fundListData, fundRules, terms)
    return allFunds
  } catch {
    return null
  }
}

const getSplittingWords = async () => {
  try {
    const words =
      splittingWords ||
      (await axios({
        method: 'GET',
        url: '/api/tw-fund/tokenizers/',
        timeout: 30000
      }))
    splittingWords = words.data ? convertTokenizers(words.data) : splittingWords
    return splittingWords || []
  } catch {
    return []
  }
}

const getFundDetail = async (shareClassId) => {
  try {
    const fundReference = await axios({
      method: 'GET',
      url: `/xapi/fundReference?scId=${shareClassId}`
    })

    return fundReference.data
  } catch {
    return null
  }
}

const getFundShareClassGroup = async (fundId) => {
  try {
    const fundShareClassGoup = await axios({
      method: 'GET',
      url: `/xapi/cos/${fundId}`
    })

    return fundShareClassGoup.data
  } catch {
    return null
  }
}

const getFundOverview = async (shareClassId) => {
  try {
    const fundOverviewData = await axios({
      method: 'GET',
      url: `/xapi/fundSummary?scId=${shareClassId}`
    })

    const formattedOverviewData = formatFundDetailOverview(fundOverviewData.data)

    return formattedOverviewData
  } catch {
    return null
  }
}

const getFundDividend = async (shareClassId) => {
  try {
    const fundDividendData = await axios({
      method: 'GET',
      url: `/xapi/fundDividend?scId=${shareClassId}`
    })

    const formattedFundDetailDividendData = formatFundDetailDividend(fundDividendData.data)

    return formattedFundDetailDividendData
  } catch {
    return null
  }
}

const getFundHoldings = async (shareClassId, holdingsType, assetClassId, isGFSTX) => {
  try {
    const fundHoldingsData = await axios({
      method: 'GET',
      url: `/xapi/fundAllocation?scId=${shareClassId}`
    })

    const formattedFundDetailHoldingsData = formatFundDetailHoldings(fundHoldingsData.data, holdingsType, assetClassId, isGFSTX)

    return formattedFundDetailHoldingsData
  } catch {
    return null
  }
}

const getFundDocument = async (shareClassId) => {
  try {
    const fundDocumentData = await axios({
      method: 'GET',
      url: `/xapi/fundDocument?scId=${shareClassId}`
    })

    const formattedFundDetailDocument = formatFundDetailDocument(fundDocumentData.data)

    return formattedFundDetailDocument
  } catch {
    return null
  }
}

const getFundESG = async (shareClassId, isOnshoreFund) => {
  try {
    const esgFundData = await axios({
      method: 'GET',
      url: `/xapi/esgFund?scId=${shareClassId}`
    })

    const formattedFundDetailESG = formatFundDetailESG(esgFundData.data, isOnshoreFund)
    return formattedFundDetailESG
  } catch {
    return null
  }
}

const getPerformanceChartData = async (shareClassId) => {
  try {
    const performanceChartData = await axios({
      method: 'GET',
      url: `/xapi/fundConvertedPrice?scId=${shareClassId}`
    })

    return performanceChartData.data
  } catch {
    return { hasError: true }
  }
}

const getNavChartData = async (shareClassId) => {
  try {
    const navChartData = await axios({
      method: 'GET',
      url: `/xapi/fundPrice?scId=${shareClassId}`
    })

    return navChartData.data
  } catch {
    return { hasError: true }
  }
}

const getAnnualReturnData = async (shareClassId) => {
  try {
    const annualReturnData = await axios({
      method: 'GET',
      url: `/xapi/fundPerformance/annualFundPerformance?scId=${shareClassId}`
    })

    const formattedAnnualReturnData = formatAnnualReturnData(annualReturnData.data)

    return formattedAnnualReturnData
  } catch {
    return null
  }
}

const getContentApiData = async (scId) => {
  try {
    const { data: contentData } = await axios({
      method: 'GET',
      url: `/api/contents/fund/related-data/?scId=${scId}&country=TWN`
    })

    contentApiData = contentData
    return contentData
  } catch (error) {
    return null
  }
}

const isCompositionNAV = () => {
  return (
    contentApiData?.data
      ?.flatMap((item) => item.data)
      ?.filter((item) => item.itemName === 'NAV Template' && item.optionValue === 'Composition NAV')?.length > 0 ?? false
  )
}

const getContentEsgFund = () => {
  return (
    contentApiData?.data?.flatMap((item) => item.data)?.filter((item) => item.itemId === '1' && item.optionValue === 'YES')?.length > 0 ??
    false
  )
}

const getEsgDownloadData = () => {
  const esgDownloadData = contentApiData?.data
    ?.flatMap((item) => item.data)
    ?.filter((item) => item.itemId === '101' && item.itemName === 'ESG Document')[0]

  return {
    downloadLabel: esgDownloadData?.optionId,
    downloadUrl: esgDownloadData?.optionValue
  }
}

const getEsgDisclosureData = () => {
  const esgDisclosureData = contentApiData?.data
    ?.flatMap((item) => item.data)
    ?.filter((item) => item.itemId === '102' && item.itemName === 'ESG Fund Data')[0]

  return esgDisclosureData
    ? {
        downloadLabel: esgDisclosureData?.optionId,
        downloadUrl: esgDisclosureData?.optionValue
      }
    : {}
}

const getFocusFundPDF = () => {
  const esgDownloadData = contentApiData?.data
    ?.flatMap((item) => item.data)
    ?.filter((item) => item.itemId === '100' && item.itemName === 'Focus Fund PDF')[0]

  return {
    downloadLabel: esgDownloadData?.optionId,
    downloadUrl: esgDownloadData?.optionValue
  }
}

const getCumulativeReturnData = async (shareClassId) => {
  try {
    const cumulativeReturnData = await axios({
      method: 'GET',
      url: `/xapi/fundPerformance/monthlyFundPerformance?scId=${shareClassId}`
    })
    const formattedCumulativeReturnData = formatCumulativeReturnData(cumulativeReturnData.data)

    return formattedCumulativeReturnData
  } catch {
    return null
  }
}

const getDividendRecords = async () => {
  try {
    const { terms } = getGlobalParams()
    const dividendRecordsData = await axios({
      method: 'GET',
      url: '/xapi/fundDividend/all'
    })

    const { data, documents } = dividendRecordsData.data
    const recordsData = convert(data, dividendRule, terms)
    const docData = formatDividendDocuments(documents)
    return { data: recordsData, documents: docData }
  } catch {
    return null
  }
}

const downloadFundsList = async (params) => {
  try {
    const downloadedFundsData = await axios({
      method: 'POST',
      url: '/xapi/fundList/excel',
      data: params,
      responseType: 'arraybuffer'
    })

    let filename = `Pricing_and_Performance_Data-${format(new Date(), 'yyyy-MM-dd')}.xlsx`

    const disposition = downloadedFundsData.headers['content-disposition']
    if (disposition && disposition.indexOf('attachment') !== -1) {
      const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
      const matches = filenameRegex.exec(disposition)
      if (matches != null && matches[1]) {
        filename = matches[1].replace(/['"]/g, '')
      }
    }

    const url = window.URL.createObjectURL(new Blob([downloadedFundsData.data]))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', `${filename}`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)

    return null
  } catch {
    return null
  }
}

const addToRecentView = (viewedFunds) => {
  const recentViewFundsStorageName = `${window.location.hostname}_recentViewFunds`
  const recentViewStorageItems = sessionStorage.getItem(recentViewFundsStorageName)
    ? JSON.parse(sessionStorage.getItem(recentViewFundsStorageName))
    : []
  if (!_.find(recentViewStorageItems, (fund) => fund.scId === viewedFunds.scId)) {
    recentViewStorageItems.push(viewedFunds)
  }

  if (recentViewStorageItems.length > 5) {
    recentViewStorageItems.shift()
  }

  sessionStorage.setItem(recentViewFundsStorageName, JSON.stringify(recentViewStorageItems))
}

const getFundsComparisonListData = async (shareClassIds) => {
  try {
    const fundOverviewData = await axios({
      method: 'GET',
      url: `/xapi/fundSummary/list?scIds=${shareClassIds}`
    })

    const formattedComparisonData = fundOverviewData.data
    return formattedComparisonData
  } catch {
    return null
  }
}

const getFundsComparisonPerformanceListData = async (shareClassIds) => {
  try {
    const annualReturnData = await axios({
      method: 'GET',
      url: `/xapi/fundPerformance/annualFundPerformances?scIds=${shareClassIds}`
    })

    const formattedAnnualReturnData = annualReturnData.data
    return formattedAnnualReturnData
  } catch {
    return null
  }
}

const getFundsComparisonPerformanceChartData = async (shareClassIds) => {
  try {
    const performanceChartData = await axios({
      method: 'GET',
      url: `/xapi/fundConvertedPrices?scIds=${shareClassIds}`
    })

    const chartData = performanceChartData.data
    return chartData
  } catch {
    return null
  }
}

export {
  getFundList,
  getSplittingWords,
  getFundDetail,
  getFundShareClassGroup,
  getFundOverview,
  getFundDividend,
  getFundHoldings,
  getPerformanceChartData,
  getNavChartData,
  getFundDocument,
  getFundESG,
  getContentEsgFund,
  getAnnualReturnData,
  isCompositionNAV,
  getCumulativeReturnData,
  getDividendRecords,
  downloadFundsList,
  addToRecentView,
  getFundsComparisonListData,
  getFundsComparisonPerformanceListData,
  getFundsComparisonPerformanceChartData,
  getFocusFundPDF,
  getContentApiData,
  getEsgDownloadData,
  getEsgDisclosureData
}
